/**
 * Recurrence helpers for Binder schedules. Schedules store a raw iCalendar
 * RFC 5545 `rrule` string (per the data-model spec) so calendaring/notifications
 * can be added later with no migration. The web UI, however, only *authors* a
 * constrained subset — `FREQ={DAILY|WEEKLY|MONTHLY|YEARLY};INTERVAL=n` — through
 * a simple frequency picker. Any rrule outside that subset (e.g. one authored by
 * a future richer editor, or `BYDAY=` rules) is treated as **custom**: shown
 * verbatim and never silently rewritten.
 *
 * No third-party rrule library — this hand-rolls parse/format/next-due for the
 * subset above. All functions are pure and side-effect free.
 */

export type Frequency = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly';

const FREQ_TO_ICAL: Record<Exclude<Frequency, 'none'>, string> = {
  daily: 'DAILY',
  weekly: 'WEEKLY',
  monthly: 'MONTHLY',
  yearly: 'YEARLY',
};

const ICAL_TO_FREQ: Record<string, Exclude<Frequency, 'none'>> = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export interface ParsedRecurrence {
  frequency: Frequency;
  interval: number;
  /** True when the stored rrule can't be mapped to the simple picker. */
  custom: boolean;
  /** The raw rrule string, present only when `custom`. */
  raw?: string;
}

/** Build a constrained rrule from the picker state. `none` → undefined (a
 *  one-time schedule stores only `dtstart`). Interval is clamped to >= 1. */
export function buildRrule(frequency: Frequency, interval: number): string | undefined {
  if (frequency === 'none') return undefined;
  const n = Number.isFinite(interval) && interval >= 1 ? Math.floor(interval) : 1;
  return `FREQ=${FREQ_TO_ICAL[frequency]};INTERVAL=${n}`;
}

/** Parse a stored rrule into picker state. Recognises only
 *  `FREQ={DAILY|WEEKLY|MONTHLY|YEARLY}` with an optional numeric `INTERVAL`;
 *  anything else (extra parts, unknown FREQ, missing FREQ) is reported as
 *  `custom` with the raw string preserved. */
export function parseRrule(rrule?: string): ParsedRecurrence {
  if (!rrule || !rrule.trim()) return { frequency: 'none', interval: 1, custom: false };

  const parts = rrule.trim().replace(/^RRULE:/i, '').split(';').map((s) => s.trim()).filter(Boolean);
  const map = new Map<string, string>();
  for (const p of parts) {
    const eq = p.indexOf('=');
    if (eq <= 0) return { frequency: 'none', interval: 1, custom: true, raw: rrule };
    map.set(p.slice(0, eq).toUpperCase(), p.slice(eq + 1).toUpperCase());
  }

  const freqRaw = map.get('FREQ');
  const freq = freqRaw ? ICAL_TO_FREQ[freqRaw] : undefined;
  const extraKeys = [...map.keys()].filter((k) => k !== 'FREQ' && k !== 'INTERVAL');
  if (!freq || extraKeys.length > 0) {
    return { frequency: 'none', interval: 1, custom: true, raw: rrule };
  }

  const intervalRaw = map.get('INTERVAL');
  if (intervalRaw !== undefined && !/^\d+$/.test(intervalRaw)) {
    return { frequency: 'none', interval: 1, custom: true, raw: rrule };
  }
  const interval = intervalRaw ? Math.max(1, parseInt(intervalRaw, 10)) : 1;
  return { frequency: freq, interval, custom: false };
}

/** Human-readable recurrence summary, e.g. "Monthly", "Every 2 weeks",
 *  "Does not repeat", or the raw rrule for a custom rule. */
export function describeRecurrence(rrule?: string): string {
  const parsed = parseRrule(rrule);
  if (parsed.custom) return parsed.raw ?? 'Custom recurrence';
  if (parsed.frequency === 'none') return 'Does not repeat';
  const { frequency, interval } = parsed;
  if (interval === 1) {
    return { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly' }[frequency];
  }
  const unit = { daily: 'days', weekly: 'weeks', monthly: 'months', yearly: 'years' }[frequency];
  return `Every ${interval} ${unit}`;
}

/** Parse a `YYYY-MM-DD` (or ISO datetime with that prefix) into a local-midnight
 *  Date. Returns undefined for anything else. Local midnight avoids the UTC
 *  off-by-one that `new Date('2026-07-01')` causes in negative-offset zones. */
function parseDate(iso?: string): Date | undefined {
  if (!iso) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Format a `YYYY-MM-DD`(prefix) date as "Jul 1, 2026". Undefined for
 *  unparseable input. Deterministic (no locale/timezone dependence). */
export function formatDate(iso?: string): string | undefined {
  const d = parseDate(iso);
  if (!d) return undefined;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** Advance a date by one recurrence step. */
function addInterval(d: Date, frequency: Exclude<Frequency, 'none'>, interval: number): Date {
  const next = new Date(d);
  switch (frequency) {
    case 'daily': next.setDate(next.getDate() + interval); break;
    case 'weekly': next.setDate(next.getDate() + interval * 7); break;
    case 'monthly': next.setMonth(next.getMonth() + interval); break;
    case 'yearly': next.setFullYear(next.getFullYear() + interval); break;
  }
  return next;
}

/**
 * The next due date for a schedule, or undefined when there's nothing to compute
 * (no/one-time recurrence, custom rrule, or an unparseable `dtstart`).
 *
 * - Once completed (`lastCompletedAt` set): next due = last completion + one
 *   interval.
 * - Not yet completed: the first occurrence on the `dtstart` cadence that falls
 *   on or after `from` (default today) — so a future `dtstart` returns itself.
 */
export function nextOccurrence(
  rrule: string | undefined,
  dtstart: string | undefined,
  lastCompletedAt?: string,
  from: Date = new Date(),
): Date | undefined {
  const parsed = parseRrule(rrule);
  if (parsed.frequency === 'none' || parsed.custom) return undefined;

  const completed = parseDate(lastCompletedAt);
  if (completed) return addInterval(completed, parsed.frequency, parsed.interval);

  let d = parseDate(dtstart);
  if (!d) return undefined;
  const floor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let guard = 0;
  while (d < floor && guard < 10_000) {
    d = addInterval(d, parsed.frequency, parsed.interval);
    guard += 1;
  }
  return d;
}
